import React, { useState } from 'react';
import { useNavigate, useParams } from 'react-router';
import { Settings, FileText, Columns, MapPin, ChevronLeft, ChevronRight, ShieldCheck } from 'lucide-react';
import { useBasePath } from '../../contexts/BasePathContext';
import { useIsMobile, useIsTablet } from '../../hooks/useMobile';
import PresetSettings from './tabs/PresetSettings';
import FieldSettings from './tabs/FieldSettings';
import LocationSettings from './tabs/LocationSettings';
import GeneralSettings from './tabs/GeneralSettings';
import LicenseSettings from './tabs/LicenseSettings';
import { Button } from '@exiftrator/shared/components/ui';
import { cn } from '../../components/utils/cn';

function PreferencesContent({ activeTab, onBack }) {
  const navigate = useNavigate();
  const { action, itemId } = useParams();
  const { appPath } = useBasePath();
  const isMobile = useIsMobile();
  const isTablet = useIsTablet();
  const [selectedTab, setSelectedTab] = useState(activeTab);

  const tabs = [
    { id: 'general', label: 'General', icon: Settings },
    { id: 'presets', label: 'Presets', icon: FileText },
    { id: 'locations', label: 'Locations', icon: MapPin },
    { id: 'fields', label: 'Exif Fields', icon: Columns },
    { id: 'license', label: 'License', icon: ShieldCheck },
  ];

  const renderContent = () => {
    switch (activeTab) {
      case 'general':
        return <GeneralSettings />;
      case 'presets':
        return <PresetSettings editorMode={action} presetId={itemId} />;
      case 'locations':
        return <LocationSettings />;
      case 'fields':
        return <FieldSettings />;
      case 'license':
        return <LicenseSettings />;
      default:
        return <GeneralSettings />;
    }
  };

  const handleTabClick = (tabId) => {
    setSelectedTab(tabId);
    navigate(appPath(`/settings/${tabId}`), { replace: true });
  };

  // Mobile: Two-state layout (menu list vs content page)
  if (isMobile) {
    // When viewing a specific tab, show full-screen content with back button
    if (selectedTab) {
      return (
        <div className="flex flex-1 flex flex-col overflow-hidden">
          {/* Mobile header with back button */}
          <div className="flex items-center gap-2 px-4 py-3 border-b border-border-default bg-bg-secondary">
            <Button
              variant="ghost"
              size="icon"
              onClick={() => {
                setSelectedTab(null);
                onBack();
              }}
            >
              <ChevronLeft className="size-5" />
            </Button>
            <span className="font-semibold text-text-primary">
              {tabs.find(t => t.id === selectedTab)?.label || 'Settings'}
            </span>
          </div>

          {/* Full-screen content */}
          <main className="flex-1 overflow-y-auto">
            <div className="p-4">
              {renderContent()}
            </div>
          </main>
        </div>
      );
    }

    // Menu state: show full-screen list of settings
    return (
      <div className="flex flex-1 flex flex-col overflow-hidden">
        {/* Mobile header with title */}
        <div className="flex items-center gap-2 px-4 py-3 border-b border-border-default bg-bg-secondary">
          <Button
            variant="ghost"
            size="icon"
            onClick={onBack}
          >
            <ChevronLeft className="size-5" />
          </Button>
          <span className="font-semibold text-text-primary">Settings</span>
        </div>

        {/* Full-screen menu list */}
        <nav className="flex-1 overflow-y-auto p-2">
          {tabs.map(tab => {
            const Icon = tab.icon;
            return (
              <button
                key={tab.id}
                onClick={() => handleTabClick(tab.id)}
                className="w-full flex items-center gap-4 px-4 py-4 rounded-lg text-left hover:bg-bg-tertiary transition-colors border-b border-border-default"
              >
                <Icon className="size-5 text-text-secondary" />
                <span className="text-[15px] font-medium text-text-primary">{tab.label}</span>
                <ChevronRight className="size-4 ml-auto text-text-supporting" />
              </button>
            );
          })}
        </nav>

        {/* Footer */}
        <div className="p-4 border-t border-border-default text-[11px] text-text-supporting text-center">
          Version 1.0.0
        </div>
      </div>
    );
  }

  // Tablet/Desktop: Sidebar + content split
  const sidebarWidth = isTablet ? 'w-[180px]' : 'w-[220px]';
  const contentPadding = isTablet ? 'p-5' : 'p-8';

  return (
    <div className="flex flex-1 overflow-hidden">
      {/* Settings Sidebar */}
      <aside className={`${sidebarWidth} flex-shrink-0 border-r border-border-default flex flex-col bg-bg-secondary`}>
        {/* Back Button */}
        <div className="p-4 border-b border-border-default flex items-center gap-2">
          <Button
            variant="ghost"
            size="sm"
            onClick={onBack}
            className="flex-1 justify-start px-3"
          >
            <ChevronLeft data-icon="inline-start" size={16} />
            Back
          </Button>
        </div>

        {/* Navigation */}
        <nav className="flex-1 p-2 space-y-1 overflow-y-auto">
          {tabs.map(tab => {
            const Icon = tab.icon;
            const isActive = activeTab === tab.id;
            return (
              <Button
                key={tab.id}
                variant="ghost"
                onClick={() => navigate(appPath(`/settings/${tab.id}`), { replace: true })}
                className={cn("w-full justify-start gap-4 px-4 py-2 h-auto text-[13px] font-semibold", isActive ? 'bg-bg-selected text-text-primary' : 'text-text-primary hover:bg-bg-hover')}
              >
                <Icon data-icon="inline-start" size={16} className="opacity-80" />
                {tab.label}
              </Button>
            );
          })}
        </nav>

        {/* Footer */}
        <div className="p-4 border-t border-border-default text-[11px] text-text-supporting">
          <div className="text-[10px] font-sans uppercase tracking-widest mb-1 opacity-50">
            App Settings
          </div>
          <div>Version 1.0.0</div>
        </div>
      </aside>

      {/* Main Content */}
      <main className="flex-1 overflow-y-auto">
        <div className={`${contentPadding} max-w-[900px]`}>
          {renderContent()}
        </div>
      </main>
    </div>
  );
}

function Preferences() {
  const navigate = useNavigate();
  const { tab = 'general' } = useParams();

  const handleBack = () => {
    navigate(-1);
  };

  return (
    <div className="flex-1 flex bg-bg-primary overflow-hidden animate-in fade-in duration-300">
      <PreferencesContent
        activeTab={tab}
        onBack={handleBack}
      />
    </div>
  );
}

export default Preferences;
